import isEmpty from 'lodash/isEmpty';
import { useUserSettingsStore } from '@/stores/user-settings.store';
import keyBy from 'lodash/keyBy';
import get from 'lodash/get';
import { getDefaultTimeseries } from '@/reports/utils';

import {
    fetchSavedViews,
    fetchSavedView,
    createSavedView,
    deleteSavedView,
    updateSavedView,
} from '@/vuex/modules/saved-views/saved-views.api';

import { formatSavedFilter, unmapFilters, REPLAY_LIBRARY, SAVED_VIEWS_TYPES } from './saved-filters.utils';

export default {
    namespaced: true,
    state: {
        map: {
            [REPLAY_LIBRARY]: {},
        },
        error: {},
        fetching: false,
        deleting: false,
        tempSavedFilter: {},
    },
    mutations: {
        setMap (state, { map, type }) {
            state.map[type] = map;
        },
        setFetching (state, { fetching }) {
            state.fetching = fetching;
        },
        setSavedFilter (state, { savedFilter }) {
            const { id, type } = savedFilter;

            state.map[type][id] = savedFilter;
        },
        setError (state, { error }) {
            state.error = error;
        },
        removeSavedFilter (state, { savedFilter }) {
            const { id, type } = savedFilter;
            delete state.map[type][id];
        },
        setDeleting (state, { deleting }) {
            state.deleting = deleting;
        },
        setTempSavedFilter (state, { tempSavedFilter }) {
            state.tempSavedFilter = tempSavedFilter;
        },
    },
    actions: {
        async loadAll ({ commit, state }, { reFetch = false, type } = {}) {
            if (!isEmpty(state.map[type]) && !reFetch) {
                return;
            }
            try {
                commit('setFetching', { fetching: true });
                commit('setError', { error: {} });
                const fetchedSavedFilters = await fetchSavedViews(type);

                commit('setMap', { map: keyBy(fetchedSavedFilters, 'id'), type });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setFetching', { fetching: false });
            }
        },
        async loadOne ({ commit, state }, { reFetch = false, id, type }) {
            if (!reFetch && state.map[type][id]) {
                return;
            }
            try {
                commit('setFetching', { fetching: true });
                commit('setError', { error: {} });
                const savedFilter = await fetchSavedView(id);

                commit('setSavedFilter', { savedFilter });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setFetching', { fetching: false });
            }
        },
        async create ({ commit }, { savedFilter }) {
            try {
                const newSavedFilter = {
                    ...savedFilter,
                    filters: unmapFilters(savedFilter.filters),
                };
                const createdSavedFilter = await createSavedView(newSavedFilter);
                commit('setSavedFilter', { savedFilter: createdSavedFilter });

                return createdSavedFilter;
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async delete ({ commit }, { savedFilter }) {
            try {
                commit('setDeleting', { deleting: true });
                await deleteSavedView(savedFilter.id);
                commit('removeSavedFilter', { savedFilter });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setDeleting', { deleting: false });
            }
        },
        async update ({ commit, state }, { savedFilter, instantUpdate = false }) {
            let existingSavedFilter;
            try {
                const savedFilterToUpdate = {
                    ...savedFilter,
                    filters: unmapFilters(savedFilter.filters),
                };

                if (instantUpdate) {
                    const { id, type } = savedFilter;
                    existingSavedFilter = { ...state.map[type][id] };
                    // update the saved filter in the store before making the API call
                    commit('setSavedFilter', { savedFilter: savedFilterToUpdate });
                }

                const updatedSavedFilter = await updateSavedView(savedFilterToUpdate);
                commit('setSavedFilter', { savedFilter: updatedSavedFilter });
            } catch (error) {
                commit('setError', { error });
                if (existingSavedFilter) {
                    commit('setSavedFilter', { savedFilter: existingSavedFilter });
                }

                throw error;
            }
        },
        updateTempSavedFilter ({ commit }, { tempSavedFilter }) {
            commit('setTempSavedFilter', { tempSavedFilter });
        },
    },
    getters: {
        savedFiltersList: (state) => (type) => {
            return Object.values(state.map[type]).map(formatSavedFilter);
        },
        savedFilterById: (state) => (id) => {
            const type = Object.values(SAVED_VIEWS_TYPES).find((type) => get(state.map[type], id));

            if (type) return formatSavedFilter(state.map[type][id]);

            return null;
        },
        defaultReplayLibrarySavedFilter: (_state, getters) => {
            return getters.savedFiltersList(REPLAY_LIBRARY).find((savedFilter) => savedFilter.locked);
        },
        defaultReplayLibrarySavedFilterId: (_state, getters) =>
            getters.defaultReplayLibrarySavedFilter?.id || 'savedFilterId',
        tempSavedFilter: (state) => state.tempSavedFilter,
        lastSavedFilterId: (_state, getters) => {
            const lastSavedFilterSetting = useUserSettingsStore().settingByName(
                'recentReplayLibrarySavedFilter',
                true,
            );
            const { defaultReplayLibrarySavedFilterId } = getters;

            return get(lastSavedFilterSetting, 'value.id') || defaultReplayLibrarySavedFilterId;
        },
        latestSavedFilter: (state, getters) => {
            const { tempSavedFilter } = state;
            const lastSavedFilter = getters.savedFilterById(getters.lastSavedFilterId);

            let view = {
                filters: {},
            };
            if (!isEmpty(tempSavedFilter)) {
                view = { ...tempSavedFilter };
            } else if (lastSavedFilter && !isEmpty(lastSavedFilter.filters)) {
                view = { ...lastSavedFilter };
            }

            if (!view.appIds && view.filters?.appId) {
                view.appIds = view.filters.appId;
            }

            return {
                ...view,
                segmentId: get(view, 'segmentId', 'everyone'),
                filters: {
                    ...view.filters,
                    timeSeries: get(view, 'filters.timeSeries', getDefaultTimeseries()),
                },
            };
        },
    },
};
